import { useState } from "react";
import { X, ChevronRight, Sparkles, BookOpen, MessageCircle, Trophy, Target } from "lucide-react";
import { useSettings } from "../context/SettingsContext";

/**
 * 5-step onboarding walkthrough shown to new users once.
 * Also reopenable from Settings.
 */
const Onboarding = ({ onClose }) => {
  const { t, markOnboardingSeen } = useSettings();
  const [step, setStep] = useState(0);

  const steps = [
    {
      title: t("onboarding_step1"),
      body: t("onboarding_step1_body"),
      icon: BookOpen,
      color: "from-indigo-500 to-purple-500",
    },
    {
      title: t("onboarding_step2"),
      body: t("onboarding_step2_body"),
      icon: Sparkles,
      color: "from-lime-500 to-green-500",
    },
    {
      title: t("onboarding_step3"),
      body: t("onboarding_step3_body"),
      icon: Target,
      color: "from-sky-500 to-blue-500",
    },
    {
      title: t("onboarding_step4"),
      body: t("onboarding_step4_body"),
      icon: MessageCircle,
      color: "from-pink-500 to-rose-500",
    },
    {
      title: t("onboarding_step5"),
      body: t("onboarding_step5_body"),
      icon: Trophy,
      color: "from-yellow-500 to-amber-500",
    },
  ];

  const handleFinish = async () => {
    await markOnboardingSeen();
    onClose?.();
  };

  const current = steps[step];
  const Icon = current.icon;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      data-testid="onboarding-modal"
    >
      <div className="bg-white rounded-3xl max-w-lg w-full p-8 relative animate-pop-in">
        {/* Close */}
        <button
          onClick={handleFinish}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
          data-testid="onboarding-close-btn"
          aria-label="Close"
        >
          <X className="w-4 h-4 text-slate-500" />
        </button>

        {/* Welcome header for first step */}
        {step === 0 && (
          <p className="text-sm font-bold text-indigo-500 mb-2">
            {t("onboarding_welcome")}
          </p>
        )}

        {/* Icon */}
        <div
          className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${current.color} flex items-center justify-center mb-6`}
        >
          <Icon className="w-10 h-10 text-white" />
        </div>

        {/* Title + body */}
        <h2 className="font-heading font-black text-2xl text-slate-800 mb-3" data-testid="onboarding-step-title">
          {current.title}
        </h2>
        <p className="text-slate-600 text-base leading-relaxed mb-8">{current.body}</p>

        {/* Step indicators */}
        <div className="flex gap-2 mb-6">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-2 flex-1 rounded-full transition-all ${
                i === step ? "bg-indigo-500" : i < step ? "bg-indigo-300" : "bg-slate-200"
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleFinish}
            className="text-slate-400 text-sm font-medium hover:text-slate-600"
            data-testid="onboarding-skip-btn"
          >
            {t("onboarding_skip")}
          </button>
          {step < steps.length - 1 ? (
            <button
              onClick={() => setStep(step + 1)}
              className="flex items-center gap-2 px-6 py-3 bg-indigo-500 text-white font-bold rounded-xl hover:bg-indigo-600 shadow-[0_3px_0_0_#4f46e5]"
              data-testid="onboarding-next-btn"
            >
              {t("onboarding_next")}
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleFinish}
              className="flex items-center gap-2 px-6 py-3 bg-lime-500 text-white font-bold rounded-xl hover:bg-lime-600 shadow-[0_3px_0_0_#65a30d]"
              data-testid="onboarding-finish-btn"
            >
              {t("onboarding_finish")}
              <Sparkles className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
